import * as React from 'react'

import { Box, useClassNames } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import * as stylesRefs from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/index.vanilla.css'
import type {
  Column,
  ColumnsType,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import {
  dailyGridTemplateColumns,
  gridColumn,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { useShiftsToDisplay } from 'modules/shift_management/hooks/useShiftsToDisplay'
import type { EmployeeWithShifts, Shift, ShiftsRow, View } from 'modules/shift_management/lib/types'

import DailyEmptyCell from './DailyEmptyCell'
import DailyShiftCell from './DailyShiftCell'

interface Props {
  view: View
  shiftsRow: ShiftsRow
  employee: EmployeeWithShifts
  columns: ColumnsType
  contractVersions: ContractVersions
}

const ShiftsGrid = ({ view, shiftsRow, employee, columns, contractVersions }: Props) => {
  const { date, dates } = view
  const cx = useClassNames(stylesRefs)

  const filteredShifts = useUnsafeMemo(() => {
    const shifts = shiftsRow.orderedShifts
    return shifts.filter((shift) => !shift.isEndNightShiftExactMidnight(date))
  }, [shiftsRow, date])

  const { shiftsToDisplay, previewPlannedBreaks } = useShiftsToDisplay({
    shifts: filteredShifts,
    shiftsRow,
    date,
  })

  const shiftsPerColumn = columns.reduce((result: Record<number, Shift[]>, column, index) => {
    result[index] = shiftsRow.shifts.during(column.date)
    return result
  }, {})
  const ocurrencesPerColumn = Object.values(shiftsPerColumn).map((shifts) => shifts.length)
  // By default, display 1 empty cell if there is no shifts
  const maxOverlapOcurrences = Math.max(...ocurrencesPerColumn) || 1

  const hoursToAdd = columns.length - dates.length

  return (
    <>
      <Box
        className={cx('grid')}
        style={{ ...dailyGridTemplateColumns(view, hoursToAdd), height: '100%' }}
      >
        {shiftsToDisplay.map((shift: Shift, index: number) => (
          <Box
            key={shift.id || index}
            style={{ ...gridColumn(shift.startHours(date), shift.endHours(date)) }}
          >
            <DailyShiftCell
              view={view}
              employee={employee}
              shiftsRow={shiftsRow}
              shift={shift}
              preview={shift.preview}
              previewPlannedBreaks={shift.preview ? previewPlannedBreaks : undefined}
              contractVersions={contractVersions}
            />
          </Box>
        ))}

        {columns.map((column: Column, columnIndex: number) => {
          const duringShifts = shiftsPerColumn[columnIndex] ?? []
          const emptyCellsCount = maxOverlapOcurrences - duringShifts.length
          const { isNextPeriod, hour } = column

          return (
            emptyCellsCount > 0 && (
              <Box
                key={hour}
                style={{
                  ...gridColumn(hour, hour + 1),
                  gridRowEnd: `span ${emptyCellsCount}`,
                }}
              >
                <DailyEmptyCell
                  shiftsRow={shiftsRow}
                  column={column}
                  disabled={!!isNextPeriod}
                  contractVersions={contractVersions}
                />
              </Box>
            )
          )
        })}
      </Box>
    </>
  )
}

export default observer(ShiftsGrid)
